<script>
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactAPI from '../../../api/contacts';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    NextButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      customerInfosData: [],
      isLoading: false,
      fetchSequence: 0,
      hoveredOrder: null,
      popupStyle: { top: '0px', left: '0px' },
    };
  },
  computed: {
    // One entry per store. A store with orders this conversation mentions by a marketplace number
    // comes first, in the order of the endpoint, which also lists those orders first.
    sections() {
      const infos = Array.isArray(this.customerInfosData)
        ? this.customerInfosData
        : [];
      const mentioned = infos.filter(info => info.mentioned);
      const ofContact = infos
        .filter(info => !info.mentioned)
        .sort((a, b) => (a.store_name || '').localeCompare(b.store_name || ''));
      return [...mentioned, ...ofContact].map(info => ({
        key: info.store_name || '_default',
        heading: info.store_name || null,
        orders: info.recent_orders || [],
        profileUrl: info.admin_url || null,
      }));
    },
    fetchKey() {
      return [this.contact?.id, this.conversationId].join('|');
    },
  },
  watch: {
    // One watcher for everything the request depends on: switching a conversation changes several
    // of these at once, and each used to fire a request of its own.
    fetchKey: {
      immediate: true,
      handler() {
        this.fetchCustomerInfos();
      },
    },
  },
  mounted() {
    emitter.on(BUS_EVENTS.CONVERSATION_ORDERS_LINKED, this.onOrdersLinked);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.CONVERSATION_ORDERS_LINKED, this.onOrdersLinked);
  },
  methods: {
    // A mail in the conversation was linked to orders after the panel loaded.
    onOrdersLinked({ conversation_id: conversationId }) {
      if (String(conversationId) === String(this.conversationId)) {
        this.fetchCustomerInfos();
      }
    },
    async fetchCustomerInfos() {
      if (!this.contact?.id) return;
      this.fetchSequence += 1;
      const sequence = this.fetchSequence;
      this.isLoading = true;
      try {
        const response = await ContactAPI.getCustomerInfos(
          this.contact.id,
          this.conversationId
        );
        // A slow answer for the conversation the agent already left must not replace the current one.
        if (sequence === this.fetchSequence) {
          this.customerInfosData = response.data || [];
        }
      } finally {
        if (sequence === this.fetchSequence) this.isLoading = false;
      }
    },
    readableDate(dateStr) {
      const unixTime = Date.parse(dateStr) / 1000;
      return messageTimestamp(unixTime, 'd.M.yyyy');
    },
    readableDateTime(dateStr) {
      const unixTime = Date.parse(dateStr) / 1000;
      return messageTimestamp(unixTime, 'd.M.yyyy HH:mm');
    },
    hasExtraDetails(order) {
      return Boolean(
        order.external_id ||
          order.payment_type ||
          order.payment_date ||
          order.delivery_provider ||
          order.expected_date_of_delivery ||
          order.date_of_delivery ||
          order.expedition_date ||
          (order.package_numbers && order.package_numbers.length > 0)
      );
    },
    onOrderEnter(event, order) {
      if (!this.hasExtraDetails(order)) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const popupWidth = 280;
      const gap = 8;
      let left = rect.left - popupWidth - gap;
      if (left < gap) {
        left = rect.right + gap;
      }
      this.popupStyle = {
        top: `${rect.top}px`,
        left: `${left}px`,
        width: `${popupWidth}px`,
      };
      this.hoveredOrder = order;
    },
    onOrderLeave() {
      this.hoveredOrder = null;
    },
    async onCopyOrderId(orderId) {
      await copyTextToClipboard(orderId);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
  },
};
</script>

<template>
  <div class="order--history">
    <div v-if="isLoading">
      <p class="flex h-full items-center flex-col justify-center">
        {{ $t('CONVERSATION_ORDER_HISTORY.LOADING') }}
      </p>
    </div>
    <div v-else-if="sections.length === 0">
      <p class="flex h-full items-center flex-col justify-center">
        {{ $t('CONVERSATION_ORDER_HISTORY.NOT_AVAILABLE') }}
      </p>
    </div>
    <div v-else>
      <div
        v-for="section in sections"
        :key="section.key"
        class="customer-info-section"
      >
        <!-- Show heading only if there are multiple customer infos and the key is not 'default' -->
        <h3 v-if="section.heading" class="customer-info-heading">
          {{ section.heading }}
        </h3>
        <div v-if="section.orders.length === 0">
          <p class="flex h-full items-center flex-col justify-center">
            {{ $t('CONVERSATION_ORDER_HISTORY.NO_ORDERS') }}
          </p>
        </div>
        <div
          v-for="order in section.orders"
          :key="`${section.key}-${order.id}`"
          class="order--history--item"
          @mouseenter="onOrderEnter($event, order)"
          @mouseleave="onOrderLeave"
        >
          <div class="items-center flex gap-1 mb-1.5">
            <a
              :href="order.admin_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm hover:underline text-woot-400 dark:text-woot-600 hover:bg-slate-50 hover:dark:bg-slate-700 overflow-hidden whitespace-nowrap text-ellipsis"
            >
              {{ order.order_id }}
            </a>
            <NextButton
              v-tooltip="$t('CONVERSATION_ORDER_HISTORY.COPY_ORDER_ID')"
              ghost
              xs
              slate
              icon="i-lucide-clipboard"
              @click="onCopyOrderId(order.order_id)"
            />
          </div>
          <div class="flex items-center break-words pl-4">
            <fluent-icon icon="money-calculator" size="12" />
            <div class="pl-2 text-nowrap">
              {{ order.total_amount }} {{ order.currency }}
            </div>
          </div>
          <div
            v-if="order.created_at"
            class="flex items-center break-words pl-4"
          >
            <fluent-icon icon="calendar" size="12" />
            <div class="pl-2">{{ readableDate(order.created_at) }}</div>
          </div>
          <div
            v-if="order.items && order.items.length > 0"
            class="flex items-center break-words pl-4"
          >
            <fluent-icon icon="cart" size="12" />
            <div class="pl-2">
              {{ order.items[0]?.name }}
              <template v-if="order.items.length > 1">
                {{
                  $t('CONVERSATION_ORDER_HISTORY.MORE_ITEMS', {
                    count: order.items.length - 1,
                  })
                }}
              </template>
            </div>
          </div>
          <div
            v-if="order.status_name"
            class="flex items-center break-words pl-4"
          >
            <fluent-icon icon="status" size="12" />
            <div class="pl-2">{{ order.status_name }}</div>
          </div>
          <div
            v-if="order.mailship_status"
            class="flex items-center break-words pl-4 gap-2"
          >
            <fluent-icon
              v-tooltip="$t('CONVERSATION_ORDER_HISTORY.EXPEDITION_STATUS')"
              icon="briefcase"
              size="12"
            />
            <span>{{ order.mailship_status }}</span>
            <a
              v-if="order.mailship_expedition_id"
              :href="`https://ui.app.mailship.eu/user/expeditions?orderId=${order.mailship_expedition_id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="text-woot-400 dark:text-woot-600 hover:underline text-sm inline-flex items-center gap-1"
            >
              {{ $t('CONVERSATION_ORDER_HISTORY.OPEN_DETAIL') }}
              <fluent-icon icon="open" size="12" />
            </a>
          </div>
        </div>
        <div
          v-if="section.profileUrl"
          class="items-center flex group justify-between pt-2"
        >
          <a
            :href="section.profileUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-woot-400 dark:text-woot-600 group-hover:bg-slate-50 group-hover:dark:bg-slate-700 inline-block rounded-sm mb-0 break-all py-0.5 px-1"
          >
            {{ $t('CONVERSATION_ORDER_HISTORY.EXPAND') }}
          </a>
        </div>
      </div>
    </div>
    <Teleport to="body">
      <div v-if="hoveredOrder" class="order--popup" :style="popupStyle">
        <div v-if="hoveredOrder.external_id" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.EXTERNAL_ID') }}
          </span>
          <span class="order--popup--value">
            {{ hoveredOrder.external_id }}
          </span>
        </div>
        <div v-if="hoveredOrder.payment_type" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.PAYMENT_TYPE') }}
          </span>
          <span class="order--popup--value">
            {{ hoveredOrder.payment_type }}
          </span>
        </div>
        <div v-if="hoveredOrder.payment_date" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.PAYMENT_DATE') }}
          </span>
          <span class="order--popup--value">
            {{ readableDateTime(hoveredOrder.payment_date) }}
          </span>
        </div>
        <div v-if="hoveredOrder.delivery_provider" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.DELIVERY_PROVIDER') }}
          </span>
          <span class="order--popup--value">
            {{ hoveredOrder.delivery_provider }}
          </span>
        </div>
        <div
          v-if="hoveredOrder.expected_date_of_delivery"
          class="order--popup--row"
        >
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.EXPECTED_DATE_OF_DELIVERY') }}
          </span>
          <span class="order--popup--value">
            {{ readableDate(hoveredOrder.expected_date_of_delivery) }}
          </span>
        </div>
        <div v-if="hoveredOrder.date_of_delivery" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.DATE_OF_DELIVERY') }}
          </span>
          <span class="order--popup--value">
            {{ readableDate(hoveredOrder.date_of_delivery) }}
          </span>
        </div>
        <div v-if="hoveredOrder.expedition_date" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.EXPEDITION_DATE') }}
          </span>
          <span class="order--popup--value">
            {{ readableDate(hoveredOrder.expedition_date) }}
          </span>
        </div>
        <div v-if="hoveredOrder.internal_note" class="order--popup--row">
          <span class="order--popup--label">
            {{ $t('CONVERSATION_ORDER_HISTORY.INTERNAL_NOTE') }}
          </span>
          <span class="order--popup--value order--popup--note">
            {{ hoveredOrder.internal_note }}
          </span>
        </div>
        <template
          v-if="
            hoveredOrder.package_numbers &&
            hoveredOrder.package_numbers.length > 0
          "
        >
          <div
            v-for="pkg in hoveredOrder.package_numbers"
            :key="`pkg-${pkg.external_id}`"
            class="order--popup--row"
          >
            <span class="order--popup--label">
              {{ $t('CONVERSATION_ORDER_HISTORY.PACKAGE_NUMBER') }}
            </span>
            <span class="order--popup--value">
              {{ `${pkg.external_id} - ${pkg.status}` }}
            </span>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.order--history--item {
  @apply border-slate-50 dark:border-slate-700/50 border-b border-solid p-2;
  &:nth-child(2n) {
    @apply bg-slate-25 dark:bg-slate-800/50;
  }
}

.order--popup {
  @apply fixed z-50 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 shadow-lg pointer-events-none;
}

.order--popup--row {
  @apply flex justify-between gap-3 py-1 text-xs border-b border-slate-50 dark:border-slate-700/50 last:border-b-0;
}

.order--popup--label {
  @apply text-slate-500 dark:text-slate-400 whitespace-nowrap;
}

.order--popup--value {
  @apply text-slate-800 dark:text-slate-100 text-right break-words;
}

// The shop writes this remark by hand and separates its lines with newlines, so they have to survive.
// Capped because it also collects operator notes and can run long.
.order--popup--note {
  @apply whitespace-pre-line max-h-24 overflow-y-auto;
}

.customer-info-section {
  &:not(:first-child) {
    @apply mt-4;
  }
}

.customer-info-heading {
  @apply text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 px-2 py-1 rounded-sm;
}
</style>
